import { Buffer } from 'buffer';
import { Subject } from 'rxjs';
import { debug } from './debug';
import {
  ClientAdapterInterface,
  RelayServerInterface,
  RelayServerOptions,
  ServerAdapterInterface,
  SocketAdapterEvent,
} from '@iotize/tap/relay/api';
import { bufferToHexString } from '@iotize/common/byte-converter';

const TAG = 'Relay';

export class RelayServer implements RelayServerInterface {
  protected _clients: ClientAdapterInterface[];

  protected _subscription?: Subject<any>;
  protected _params: RelayServerOptions;

  constructor(
    protected _server: ServerAdapterInterface,
    params?: RelayServerOptions,
  ) {
    this._params = params || {
      port: 2000,
    };
    this._clients = [];
  }

  get server(): ServerAdapterInterface {
    return this._server;
  }

  addClient(socket: ClientAdapterInterface): this {
    this._clients.push(socket);
    return this;
  }

  removeClient(client: ClientAdapterInterface): this {
    let index = this._clients.indexOf(client);
    if (index >= 0) {
      client.close();
      debug(TAG, `Removing client ${client.getClientName()}...`);
      delete this._clients[index];
    }
    return this;
  }

  public start(): Promise<any> {
    this._subscription = new Subject<any>();
    this._server
      .connections()
      .subscribe(async (socket: ClientAdapterInterface) => {
        // Identify this client
        let clientName = socket.getClientName();
        debug(TAG, `[NEW_CLIENT] ${clientName}`);

        await socket.ready();
        debug(TAG, `[NEW_CLIENT] ${clientName} IS READY`);

        // Put this new client in the list
        this.addClient(socket);

        socket.events().subscribe({
          next: (event: SocketAdapterEvent) => {
            switch (event.type) {
              case 'data':
                let messageString = this.messageToLog(event.payload);
                debug(TAG, `[MESSAGE] ${messageString} from ${clientName}`);
                this.broadcastMessage(event.payload, socket);
                break;
              case 'error':
                debug(TAG, `[ERROR] ${clientName} `, event.payload);
                // Do we have to remove client every time ?
                this.removeClient(socket);
                break;
              case 'end':
                debug(TAG, `[CLIENTEND] ${clientName}`);
                this.removeClient(socket);
                break;
            }
          },
        });
      });
    return this._server.listen(this._params);
  }

  public async stop() {
    this._clients.forEach((client: ClientAdapterInterface) => {
      client.close();
    });
    if (this._server) {
      await this._server.stop();
    }
    if (this._subscription) {
      this._subscription.complete();
    }
  }

  public broadcastMessage(
    message: string | Buffer,
    sender: ClientAdapterInterface,
  ) {
    let messageString = this.messageToLog(message);
    debug(TAG, `[BROADCAST] ${messageString} from ${sender.getClientName()}`);
    if (this._clients.length == 0) {
      debug(TAG, 'There is no client yet');
      return;
    }
    // Send a message to all clients
    this._clients.forEach(function (client: ClientAdapterInterface) {
      // Don't want to send it to sender
      try {
        if (client === sender) {
          debug(TAG, `\t- Skip sender ${client.getClientName()}`);
          return;
        }
        if (client.isWritable()) {
          debug(TAG, `\t- Sent to ${client.getClientName()}`);
          client.write(message);
        } else {
          debug(
            TAG,
            `Client ${client.getClientName()} is not writable. Cannot send to it`,
          );
        }
      } catch (err) {
        debug(`Cannot broadcast to ${client.getClientName()}`, err);
      }
    });
  }

  protected messageToLog(message: string | Buffer | any): string {
    if (typeof message === 'string') {
      return `"${message}"`;
    } else if (message instanceof Buffer) {
      return bufferToHexString(message);
    } else {
      return message;
    }
  }
}
